import React from "react";
import L from "leaflet";
import { Marker } from "react-leaflet";

const BOAT_ANIMATION_MIN_MS = 2200;
const BOAT_ANIMATION_MAX_MS = 5500;
const BOAT_ANIMATION_MS_PER_METER = 0.04;

const easeOutCubic = (progress) => 1 - (1 - progress) ** 3;

const getRouteLengthMeters = (points = []) => {
  if (points.length < 2) return 0;

  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += L.latLng(points[i - 1].lat, points[i - 1].lng).distanceTo(
      L.latLng(points[i].lat, points[i].lng)
    );
  }
  return total;
};

const getAnimationDuration = (points = []) => {
  const meters = getRouteLengthMeters(points);
  return Math.min(
    BOAT_ANIMATION_MAX_MS,
    Math.max(BOAT_ANIMATION_MIN_MS, meters * BOAT_ANIMATION_MS_PER_METER)
  );
};

const interpolateAlongPath = (points = [], progress = 0) => {
  if (!points.length) return null;
  if (points.length === 1 || progress <= 0) {
    return { lat: points[0].lat, lng: points[0].lng };
  }
  if (progress >= 1) {
    const last = points[points.length - 1];
    return { lat: last.lat, lng: last.lng };
  }

  const segmentLengths = [];
  let total = 0;

  for (let i = 1; i < points.length; i += 1) {
    const length = L.latLng(points[i - 1].lat, points[i - 1].lng).distanceTo(
      L.latLng(points[i].lat, points[i].lng)
    );
    segmentLengths.push(length);
    total += length;
  }

  if (total === 0) {
    const last = points[points.length - 1];
    return { lat: last.lat, lng: last.lng };
  }

  const target = total * progress;
  let walked = 0;

  for (let i = 0; i < segmentLengths.length; i += 1) {
    const segmentLength = segmentLengths[i];
    if (walked + segmentLength >= target) {
      const segmentProgress = (target - walked) / segmentLength;
      const start = points[i];
      const end = points[i + 1];
      return {
        lat: start.lat + (end.lat - start.lat) * segmentProgress,
        lng: start.lng + (end.lng - start.lng) * segmentProgress,
      };
    }
    walked += segmentLength;
  }

  const last = points[points.length - 1];
  return { lat: last.lat, lng: last.lng };
};

const buildAnimationPath = (origin, current, seaRoutes) => {
  if (!seaRoutes) return [];

  const completed = seaRoutes.completedRoute || [];
  if (completed.length >= 2) return completed;
  if (origin && current) return [origin, current];
  return [];
};

const ShipmentAnimatedBoatMarker = ({
  origin,
  current,
  seaRoutes,
  animationKey,
  icon,
  children,
}) => {
  const markerRef = React.useRef(null);
  const [position, setPosition] = React.useState(null);

  const animationPath = React.useMemo(
    () => buildAnimationPath(origin, current, seaRoutes),
    [origin, current, seaRoutes]
  );

  const pathKey = React.useMemo(() => {
    if (!animationPath.length) return "";
    return `${animationKey}-${animationPath
      .map((point) => `${point.lat},${point.lng}`)
      .join("|")}`;
  }, [animationKey, animationPath]);

  React.useEffect(() => {
    if (!current) {
      setPosition(null);
      return undefined;
    }

    if (animationPath.length < 2) {
      setPosition(origin || current);
      return undefined;
    }

    const startPoint = animationPath[0];
    const endPoint = animationPath[animationPath.length - 1];
    setPosition(startPoint);

    const duration = getAnimationDuration(animationPath);
    const startTime = performance.now();
    let frameId = 0;

    const animate = (timestamp) => {
      const elapsed = timestamp - startTime;
      const linearProgress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(linearProgress);
      const nextPosition = interpolateAlongPath(animationPath, easedProgress);

      if (nextPosition) {
        setPosition(nextPosition);
      }

      if (linearProgress < 1) {
        frameId = window.requestAnimationFrame(animate);
      } else {
        setPosition(endPoint);
      }
    };

    frameId = window.requestAnimationFrame(animate);

    return () => window.cancelAnimationFrame(frameId);
  }, [pathKey, current, animationPath]);

  if (!current || !position) return null;

  return (
    <Marker
      ref={markerRef}
      position={[position.lat, position.lng]}
      icon={icon}
      eventHandlers={{
        mouseover: () => markerRef.current?.openPopup(),
        mouseout: () => markerRef.current?.closePopup(),
      }}
    >
      {children}
    </Marker>
  );
};

export default ShipmentAnimatedBoatMarker;
